import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import Lottie from "lottie-react";
import { FiAlertCircle, FiArrowRight, FiCheck, FiSend } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import gmail from "../../assets/gmail.json";
import phone from "../../assets/phone.json";
import maps from "../../assets/maps.json";
import { EMAIL, PHONE, PHONE_LABEL, whatsappLink } from "../../contact";
import QrLinks from "./CodeQR";
import NewsLetter from "./Newsletter";
import "./Contact.css";

const RULES = {
  user_name: (v) => (v.trim().length >= 2 ? "" : "Veuillez entrer votre nom."),
  user_email: (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? "" : "Adresse email invalide.",
  subject: (v) => (v.trim().length >= 3 ? "" : "Veuillez indiquer un sujet."),
  message: (v) =>
    v.trim().length >= 10 ? "" : "Votre message est trop court (10 caractères minimum).",
};

const METHODS = [
  { data: maps, title: "Localisation", text: "Casablanca, Tit Mellil" },
  { data: gmail, title: "Email", text: EMAIL, link: `mailto:${EMAIL}` },
  { data: phone, title: "Téléphone", text: PHONE_LABEL, link: `tel:${PHONE}` },
];

function Field({ name, label, type = "text", textarea, value, touched, onChange, onBlur }) {
  const error = RULES[name](value);
  const state = touched ? (error ? "invalid" : "valid") : "";
  const Tag = textarea ? "textarea" : "input";

  return (
    <div className={`ct-field ${state}`}>
      <Tag
        id={`ct-${name}`}
        name={name}
        type={textarea ? undefined : type}
        rows={textarea ? 5 : undefined}
        placeholder=" "
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={state === "invalid"}
        aria-describedby={state === "invalid" ? `ct-${name}-error` : undefined}
      />
      <label htmlFor={`ct-${name}`}>{label}</label>
      <span className="ct-field__icon" aria-hidden="true">
        {state === "valid" && <FiCheck />}
        {state === "invalid" && <FiAlertCircle />}
      </span>
      <AnimatePresence initial={false}>
        {state === "invalid" && (
          <motion.p
            className="ct-field__error"
            id={`ct-${name}-error`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

function SuccessMessage({ onReset }) {
  return (
    <motion.div
      className="ct-success"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <svg viewBox="0 0 52 52" className="ct-success__svg" aria-hidden="true">
        <motion.circle
          cx="26"
          cy="26"
          r="24"
          fill="none"
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
        <motion.path
          d="M15 27 l8 8 l15 -16"
          fill="none"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
        />
      </svg>
      <h2>Message envoyé !</h2>
      <p>Merci, nous vous répondrons dans les plus brefs délais.</p>
      <button type="button" className="ct-btn ct-btn--ghost" onClick={onReset}>
        Envoyer un autre message
      </button>
    </motion.div>
  );
}

function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const form = useRef();
  // Product pages link here with ?produit=<name> to pre-fill the subject.
  const [searchParams] = useSearchParams();
  const product = searchParams.get("produit");

  const [values, setValues] = useState(() => ({
    user_name: "",
    user_email: "",
    subject: product ? `Demande de devis : ${product}` : "",
    message: "",
  }));
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleChange = (e) =>
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  const handleBlur = (e) =>
    setTouched((t) => ({ ...t, [e.target.name]: true }));

  const sendEmail = (e) => {
    e.preventDefault();
    setTouched({ user_name: true, user_email: true, subject: true, message: true });
    if (Object.keys(RULES).some((name) => RULES[name](values[name]))) return;

    setStatus("sending");
    emailjs
      .sendForm("service_9813s7b", "template_dp8qcab", form.current, "uv-KwgdGWcnpCrbQM")
      .then(
        () => {
          setStatus("sent");
          setValues({ user_name: "", user_email: "", subject: "", message: "" });
          setTouched({});
        },
        () => setStatus("error")
      );
  };

  const fieldProps = (name) => ({
    name,
    value: values[name],
    touched: touched[name],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  return (
    <>
      <div className="ct">
        <header className="ct-hero">
          <motion.span
            className="ct-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Contact
          </motion.span>
          <motion.h1
            className="ct-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            Parlons de votre projet
          </motion.h1>
          <motion.p
            className="ct-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            Devis gratuit, conseils personnalisés : écrivez-nous, nous vous répondons rapidement.
          </motion.p>
        </header>

        <section className="ct-main">
          <motion.div
            className="ct-card"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {status === "sent" ? (
              <SuccessMessage onReset={() => setStatus("idle")} />
            ) : (
              <form ref={form} onSubmit={sendEmail} noValidate>
                <h2>Envoyez-nous un message</h2>
                <Field {...fieldProps("user_name")} label="Votre nom" />
                <Field {...fieldProps("user_email")} label="Votre email" type="email" />
                <Field {...fieldProps("subject")} label="Sujet" />
                <Field {...fieldProps("message")} label="Votre message" textarea />
                {status === "error" && (
                  <p className="ct-alert" role="alert">
                    L'envoi a échoué. Réessayez, ou contactez-nous directement par
                    WhatsApp ou téléphone.
                  </p>
                )}
                <motion.button
                  type="submit"
                  className="ct-btn"
                  disabled={status === "sending"}
                  whileHover={{ scale: status === "sending" ? 1 : 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {status === "sending" ? (
                    <>
                      <span className="ct-spinner" /> Envoi en cours...
                    </>
                  ) : (
                    <>
                      <FiSend /> Envoyer le message
                    </>
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>

          <div className="ct-side">
            {METHODS.map((item, i) => {
              const Wrapper = item.link ? "a" : "div";
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.1 }}
                >
                  <Wrapper
                    className="ct-method"
                    {...(item.link ? { href: item.link } : {})}
                  >
                    <div className="ct-method__icon">
                      <Lottie animationData={item.data} />
                    </div>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </Wrapper>
                </motion.div>
              );
            })}

            <motion.a
              className="ct-whatsapp"
              href={whatsappLink("Bonjour, j'ai un projet en inox et je souhaite en discuter.")}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              whileHover={{ scale: 1.02 }}
            >
              <FaWhatsapp />
              <span>Discuter sur WhatsApp</span>
              <FiArrowRight />
            </motion.a>

            <motion.div
              className="ct-map"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <iframe
                title="Cristal Inox sur la carte"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3324.870613518232!2d-7.452359590042773!3d33.55673787323632!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda63544b9850703%3A0xfe37e6634e59f4e1!2sCristalinox!5e0!3m2!1sfr!2sma!4v1741535497597!5m2!1sfr!2sma"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </motion.div>
          </div>
        </section>

        <section className="ct-faq">
          <h2>Une question avant de nous écrire ?</h2>
          <p>Délais, matériaux, devis : retrouvez les réponses les plus fréquentes.</p>
          <Link to="/FAQ" className="ct-btn ct-btn--ghost">
            Voir la FAQ <FiArrowRight />
          </Link>
        </section>
      </div>
      <QrLinks />
      <NewsLetter />
    </>
  );
}

export default Contact;
